import { Briefcase, Euro, Languages, MapPin, Users } from '@sortlist-frontend/design-system/icons';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { formatCurrency } from '@sortlist-frontend/utils';

import { rankingConfig } from '../ranking.config';
import type { RankingAgencyBase } from '../types';
import { RatingStat } from './RatingStat';

interface AgencyMetaRowProps {
  agency: Pick<RankingAgencyBase, 'rating' | 'reviewCount' | 'city' | 'otherOfficesCount' | 'budgetFrom'> & {
    country?: string;
  };
  teamSize?: string | null;
  languages?: readonly string[];
  publishedProjectsCount?: number;
  locale: string;
  isCompact?: boolean;
}

export const AgencyMetaRow = (props: AgencyMetaRowProps) => {
  const { agency, teamSize, languages, publishedProjectsCount, locale, isCompact = false } = props;
  const { t } = useTranslation(rankingConfig.i18nNamespaces);

  const location = agency.country != null ? `${agency.city}, ${agency.country}` : agency.city;
  const city =
    location != null && agency.otherOfficesCount > 0
      ? t('longtail:ranking.agency.cityWithOffices', { city: location, count: agency.otherOfficesCount })
      : location;

  const budget =
    agency.budgetFrom != null
      ? t('longtail:ranking.agency.budgetFrom', {
          budget: formatCurrency({ value: agency.budgetFrom, locale, rounded: true }),
        })
      : t('longtail:ranking.agency.budgetOnRequest');

  return (
    <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-secondary-700">
      <li>
        <RatingStat rating={agency.rating} reviewCount={agency.reviewCount} locale={locale} isCompact={isCompact} />
      </li>
      {city != null ? (
        <li className="inline-flex items-center gap-1.5">
          <MapPin size={14} className="text-secondary-400" aria-hidden="true" />
          {city}
        </li>
      ) : null}
      <li className="inline-flex items-center gap-1.5">
        <Euro size={14} className="text-secondary-400" aria-hidden="true" />
        {budget}
      </li>
      {teamSize != null ? (
        <li className="inline-flex items-center gap-1.5">
          <Users size={14} className="text-secondary-400" aria-hidden="true" />
          {t('longtail:ranking.agency.teamSize', { range: teamSize })}
        </li>
      ) : null}
      {languages != null ? (
        <li className="inline-flex items-center gap-1.5">
          <Languages size={14} className="text-secondary-400" aria-hidden="true" />
          {languages.join(', ')}
        </li>
      ) : null}
      {publishedProjectsCount != null ? (
        <li className="inline-flex items-center gap-1.5">
          <Briefcase size={14} className="text-secondary-400" aria-hidden="true" />
          {t('longtail:ranking.agency.publishedProjects', { count: publishedProjectsCount })}
        </li>
      ) : null}
    </ul>
  );
};
